import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Member } from '../../types';
import { useOrganizationalRoles } from '../../hooks';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { dateToInputValue, normalizeDateValue } from '../../utils/formatters';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { MultiSelect } from '../ui/multi-select';
import { Upload, X } from 'lucide-react';
import { TimezoneSelect } from '../ui/timezone-select';
import { OffboardingFields } from './OffboardingFields';

interface PersonFormData {
  name: string;
  email: string;
  reportsTo: string;
  organizationRole: string;
  clients: string[];
  birthDate: string;
  hireDate: string;
  contractRate: string;
  timezone: string;
  offboardingDate: string;
  offboardingReason: string;
}

interface PersonFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: PersonFormData, photo: File | null) => Promise<void>;
  editingPerson: Member | null;
  people: Member[];
  allClientTags: string[];
}

export const PersonFormDialog: React.FC<PersonFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  editingPerson,
  people,
  allClientTags,
}) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation('common');
  const { data: organizationalRoles = [] } = useOrganizationalRoles(false);
  
  // Sort roles by display order, then by name
  const sortedRoles = [...organizationalRoles].sort((a, b) => {
    if (a.displayOrder !== b.displayOrder) {
      return a.displayOrder - b.displayOrder;
    }
    return a.name.localeCompare(b.name);
  });
  
  const [formData, setFormData] = useState<PersonFormData>({
    name: '',
    email: '',
    reportsTo: '__none__',
    organizationRole: 'Engineer',
    clients: [],
    birthDate: '',
    hireDate: '',
    contractRate: '',
    timezone: '',
    offboardingDate: '',
    offboardingReason: '',
  });
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Clear error when dialog opens/closes or person changes
    setError(null);
    
    if (editingPerson) {
      setFormData({
        name: editingPerson.name,
        email: editingPerson.email,
        reportsTo: editingPerson.reportsTo?.id || '__none__',
        organizationRole: editingPerson.organizationRole || 'Engineer',
        clients: editingPerson.clients || [],
        birthDate: dateToInputValue(editingPerson.birthDate),
        hireDate: dateToInputValue(editingPerson.hireDate),
        contractRate: editingPerson.contractRate?.toString() || '',
        timezone: editingPerson.timezone || '',
        offboardingDate: dateToInputValue(editingPerson.offboardingDate),
        offboardingReason: editingPerson.offboardingReason || '',
      });
      setPhotoPreview(editingPerson.profilePictureUrl || editingPerson.photoURL || null);
      setPhoto(null);
    } else {
      setFormData({
        name: '',
        email: '',
        reportsTo: '__none__',
        organizationRole: 'Engineer',
        clients: [],
        birthDate: '',
        hireDate: '',
        contractRate: '',
        timezone: '',
        offboardingDate: '',
        offboardingReason: '',
      });
      setPhotoPreview(null);
      setPhoto(null);
    }
  }, [editingPerson, open]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert(t('form.photoSizeError'));
        return;
      }
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhoto(null);
    setPhotoPreview(editingPerson?.profilePictureUrl || editingPerson?.photoURL || null);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    
    // Client-side validation - stricter email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formData.email || !emailRegex.test(formData.email.trim())) {
      setError('Please enter a valid email address (e.g., user@example.com)');
      setSubmitting(false);
      return;
    }
    
    try {
      await onSubmit(formData, photo);
    } catch (err: any) {
      console.error('Submit error:', err);
      // Handle NestJS validation errors (array of messages)
      if (err.response?.data?.message && Array.isArray(err.response.data.message)) {
        setError(err.response.data.message.join(', '));
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err.response?.status === 400) {
        setError('Invalid data. Please check all fields and try again.');
      } else {
        setError(err.message || 'Failed to save person');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="person-form-dialog">
        <DialogHeader>
          <DialogTitle data-testid="person-form-dialog-title">
            {editingPerson ? t('form.editTitle') : t('form.addTitle')}
          </DialogTitle>
          <DialogDescription data-testid="person-form-dialog-description">
            {editingPerson
              ? t('form.editDescription')
              : t('form.addDescription')}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive" data-testid="person-form-error-alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4" data-testid="person-form-fields">
          {/* Photo Upload */}
          <div className="flex flex-col items-center gap-4" data-testid="person-form-photo-section">
            <Avatar className="h-24 w-24" data-testid="person-form-avatar">
              {photoPreview && <AvatarImage src={photoPreview} />}
              <AvatarFallback className="bg-primary/10 text-primary text-2xl">
                {formData.name
                  ? formData.name
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                      .toUpperCase()
                      .slice(0, 2)
                  : '?'}
              </AvatarFallback>
            </Avatar>
            <div className="flex gap-2">
              <label>
                <Button type="button" variant="outline" size="sm" asChild data-testid="person-form-button-upload-photo">
                  <span className="cursor-pointer">
                    <Upload className="h-4 w-4 mr-2" />
                    {t('form.uploadPhoto')}
                  </span>
                </Button>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  data-testid="person-form-input-photo"
                />
              </label>
              {photoPreview && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleRemovePhoto}
                  data-testid="person-form-button-remove-photo"
                >
                  <X className="h-4 w-4 mr-2" />
                  {t('form.removePhoto')}
                </Button>
              )}
            </div>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('form.labels.name')} *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t('form.placeholders.name')}
                required
                data-testid="person-form-input-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('form.labels.email')} *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder={t('form.placeholders.email')}
                required
                data-testid="person-form-input-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="organizationRole">{t('form.labels.organizationRole')} *</Label>
              <Select
                value={formData.organizationRole}
                onValueChange={(value) =>
                  setFormData(prev => ({ ...prev, organizationRole: value }))
                }
              >
                <SelectTrigger id="organizationRole" data-testid="person-form-select-organization-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {sortedRoles.map((role) => (
                    <SelectItem key={role.id} value={role.name} data-testid={`person-form-option-role-${role.id}`}>
                      <div className="flex items-center gap-2">
                        {role.name}
                        {role.isAdmin && (
                          <Badge variant="default" className="bg-purple-600 text-xs">
                            Admin
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reportsTo">{t('form.labels.reportsTo')}</Label>
              <Select
                value={formData.reportsTo}
                onValueChange={(value) =>
                  setFormData(prev => ({ ...prev, reportsTo: value }))
                }
              >
                <SelectTrigger id="reportsTo" data-testid="person-form-select-reports-to">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" data-testid="person-form-option-reports-to-none">{tCommon('none')}</SelectItem>
                  {people
                    .filter((p) => p.id !== editingPerson?.id && p.isActive !== false)
                    .map((person) => (
                      <SelectItem key={person.id} value={person.id} data-testid={`person-form-option-reports-to-${person.id}`}>
                        {person.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="timezone">Timezone</Label>
              <TimezoneSelect
                value={formData.timezone}
                onChange={(value) => setFormData(prev => ({ ...prev, timezone: value }))}
              />
              <p className="text-xs text-almost-black/60">
                Used for scheduling celebrations at the right local time
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="birthDate">{t('form.labels.birthDate')}</Label>
              <Input
                id="birthDate"
                type="date"
                value={formData.birthDate}
                onChange={(e) =>
                  setFormData(prev => ({ ...prev, birthDate: normalizeDateValue(e.target.value) }))
                }
                data-testid="person-form-input-birth-date"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hireDate">{t('form.labels.hireDate')}</Label>
              <Input
                id="hireDate"
                type="date"
                value={formData.hireDate}
                onChange={(e) =>
                  setFormData(prev => ({ ...prev, hireDate: normalizeDateValue(e.target.value, new Date().getFullYear()) }))
                }
                data-testid="person-form-input-hire-date"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contract-rate">Contract Rate</Label>
              <Input
                id="contract-rate"
                type="number"
                step="0.01"
                min="0"
                value={formData.contractRate}
                onChange={(e) => setFormData(prev => ({ ...prev, contractRate: e.target.value }))}
                placeholder="0.00"
                data-testid="person-form-input-contract-rate"
              />
            </div>

            {editingPerson?.isActive === false && (
              <OffboardingFields
                offboardingDate={formData.offboardingDate}
                offboardingReason={formData.offboardingReason}
                onDateChange={(value) =>
                  setFormData(prev => ({
                    ...prev,
                    offboardingDate: normalizeDateValue(value, new Date().getFullYear()),
                  }))
                }
                onReasonChange={(value) =>
                  setFormData(prev => ({ ...prev, offboardingReason: value }))
                }
                idPrefix="person-form-offboarding"
                testIdPrefix="person-form-offboarding"
              />
            )}

            <div className="col-span-2 space-y-2">
              <Label htmlFor="clients">{t('form.labels.clients')}</Label>
              <MultiSelect
                options={allClientTags}
                value={formData.clients}
                onChange={(value) => setFormData(prev => ({ ...prev, clients: value }))}
                placeholder={t('form.placeholders.clients')}
                data-testid="person-form-select-clients"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="person-form-button-cancel">
            {tCommon('buttons.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="person-form-button-submit">
            {submitting ? tCommon('saving') : editingPerson ? tCommon('buttons.update') : tCommon('buttons.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
